import { useCallback, useEffect, useRef, useState } from "react";
import Layout from "../../layout/Layout";
import CouponCodeModal from "./CouponCodeModal";
import {
  Form,
  InputGroup,
  OverlayTrigger,
  ProgressBar,
  Tooltip,
} from "react-bootstrap";
import { useGlobalState } from "../../context/GlobalStateContext";
import axiosInstance from "../../utils/axiosConfig";
import * as CustomToast from "../../components/CustomToastMessage";
import CustomPagination from "../../components/CustomPagination";
import Loader from "../../components/loader/Loader";
import DeleteModal from "../../components/DeleteModal";
import { Link } from "react-router-dom";
import RedemptionsUsersListViewModel from "./RedemptionsUsersListViewModel";

function CouponCode() {
  const [show, setShow] = useState({
    couponCode: false,
    redemptionsUsersList: false,
    data: null,
  });

  const toggleModelVisibility = (modal, data = null) => {
    setShow((prev) => ({ ...prev, [modal]: !prev[modal], data }));
  };

  const [couponCodeList, setCouponCodeList] = useState([]);

  const { loading, setLoading, pagination, setPagination, handleLimitChange } =
    useGlobalState();
  const [activeCode, setActiveCode] = useState(0);
  const [totalRedemptions, setTotalRedemptions] = useState(0);
  const [search, setSearch] = useState("");
  const [promoCodeAccess, setPromoCodeAccess] = useState(false);
  const fetchCoupons = useCallback(
    async (page = 1, limit = 10, searchQuery = "") => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/promo-codes`,
          {
            params: {
              page,
              limit,
              search: searchQuery,
            },
          }
        );
        if (response.data.success) {
          setCouponCodeList(response.data.data.promoCodes);
          setPagination(response.data.pagination);
          setActiveCode(response.data.data.activePromoCodesCount);
          setTotalRedemptions(response.data.data.totalRedemptionsCount);
        } else {
          setCouponCodeList([]);
          setPagination((prev) => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
          }));
        }
      } catch (error) {
        CustomToast.error(
          error.response?.data?.message || "Failed to fetch promo codes."
        );
        console.error(error.response?.data?.message || "Internal server error");
        setCouponCodeList([]);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        }));
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setPagination]
  );

  const timeoutIdRef = useRef(null);
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      fetchCoupons(pagination.currentPage, pagination.limit, value);
    }, 15000);
  };

  useEffect(() => {
    fetchCoupons(pagination.currentPage, pagination.limit, search);
    const fetchPromoCodeAccess = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/promo/get`
        );
        if (response.data.success) {
          setPromoCodeAccess(response.data.data.promoCodeApply);
        }
      } catch (error) {
        console.error("Failed to fetch promo code access:", error);
      }
    };
    fetchPromoCodeAccess();
  }, [pagination.currentPage, pagination.limit, search, fetchCoupons]);

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/promo-code/${id}`
      );
      if (response.data.success) {
        CustomToast.success(response.data.message);
        fetchCoupons(pagination.currentPage, pagination.limit, search);
      }
    } catch (error) {
      CustomToast.error(
        error.response?.data?.message || "Failed to delete promo code."
      );
      console.error(error.response?.data?.message || "Internal server error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActiveStatus = async (id) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/promo-code/toggle/${id}`
      );
      if (response.data.success) {
        setCouponCodeList((prevList) =>
          prevList.map((item) =>
            item._id === id
              ? { ...item, isActive: response.data.data.isActive }
              : item
          )
        );
        fetchCoupons(pagination.currentPage, pagination.limit, search);
        CustomToast.success(response.data.message);
      }
    } catch (error) {
      CustomToast.error(
        error.response?.data?.message || "Failed to update status."
      );
      console.error(error.response?.data?.message || "Internal server error");
    }
  };
  const handleCopyCode = (code) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        CustomToast.success("Coupon code copied to clipboard!");
      })
      .catch((error) => {
        CustomToast.error("Failed to copy coupon code.");
        console.error(error);
      });
  };
  const [visibleCodes, setVisibleCodes] = useState({});
  const handleToggleVisibility = (code) => {
    setVisibleCodes((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const handleTogglePromocodeAccess = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/promo/upsert`,
        {
          promoCodeApply: !promoCodeAccess,
        }
      );
      if (response.data.success) {
        setPromoCodeAccess(response.data.data.promoCodeApply);
        CustomToast.success(response.data.message);
      }
    } catch (error) {
      CustomToast.error(
        error.response?.data?.message || "Failed to update promo code access."
      );
      console.error(error.response?.data?.message || "Internal server error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewUsers = (data) => {
    toggleModelVisibility("redemptionsUsersList", data);
  };

  return (
    <Layout title="Coupon Code">
      <main>
        <div className="card page-card">
          <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <p>Show</p>
              <Form.Select
                aria-label="Default select example"
                value={pagination.limit}
                onChange={handleLimitChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </Form.Select>
              <p>entries</p>
            </div>
            <div className="d-flex flex-column flex-md-row  align-items-start align-items-md-center gap-3 sm-w-100">
              <div className="d-flex align-items-center gap-2">
                <p>Promocode Access</p>
                <Form.Check
                  type="switch"
                  id={`active-switch`}
                  className="custom-switch"
                  checked={promoCodeAccess}
                  onChange={handleTogglePromocodeAccess}
                />
              </div>
              <div className="header-search">
                <InputGroup>
                  <InputGroup.Text id="basic-addon1" className="bg-transparent">
                    <img src="/images/png/search.png" width={20} alt="" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    className="border-start-0 ps-0"
                    placeholder="search here..."
                    value={search}
                    onChange={handleSearch}
                  />
                </InputGroup>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="free-access-code">
              <div className="row">
                <div className="col-md-8">
                  <h5>Free Coupon Code</h5>
                  <p>Generate and manage codes for free user access</p>
                </div>
                <div className="col-md-4">
                  <div className="row">
                    <div className="col-6">
                      <p>Active Code</p>
                      <h5>{activeCode}</h5>
                    </div>
                    <div className="col-6">
                      <p>Total Redemptions</p>
                      <h5>{totalRedemptions}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-start align-items-center gap-2 mt-3">
                <button
                  className="btn-primary fs-16 fw-600"
                  onClick={() => toggleModelVisibility("couponCode")}
                >
                  Generate New Code
                </button>
                <button
                  className="btn-outline edit-hover fs-16 px-3 fw-700 d-flex align-items-center gap-2"
                  onClick={() =>
                    fetchCoupons(
                      pagination.currentPage,
                      pagination.limit,
                      search
                    )
                  }
                >
                  <img src="/images/png/refresh.png" width={20} alt="refresh" />
                  Refresh
                </button>
              </div>
            </div>
            <hr />
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <Loader />
              </div>
            ) : Array.isArray(couponCodeList) && couponCodeList.length > 0 ? (
              couponCodeList.map((item) => {
                const usageProgress =
                  item.maxUses > 0 ? (item.timesUsed / item.maxUses) * 100 : 0;
                return (
                  <div
                    key={item._id}
                    className="card page-card mt-4 coupon-code-details"
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <span>
                            {visibleCodes[item.code] ? item.code : "••••••••••"}
                          </span>
                          <button
                            className={
                              item.isActive &&
                              new Date(item.expiresAt) > new Date()
                                ? "active-btn"
                                : !item.isActive &&
                                  new Date(item.expiresAt) > new Date()
                                ? "inactive-btn"
                                : "inactive-btn"
                            }
                          >
                            {item.isActive &&
                            new Date(item.expiresAt) > new Date()
                              ? "Active"
                              : !item.isActive &&
                                new Date(item.expiresAt) > new Date()
                              ? "Inactive"
                              : "Expired"}
                          </button>

                          <img
                            src={
                              visibleCodes[item.code]
                                ? "/images/png/eye-close.png"
                                : "/images/png/view.png"
                            }
                            className="pointer-cursor"
                            width={40}
                            height={40}
                            alt="toggle visibility"
                            onClick={() => handleToggleVisibility(item.code)}
                          />
                          <img
                            src="/images/png/copy.png"
                            className="pointer-cursor"
                            onClick={() => handleCopyCode(item.code)}
                            width={40}
                            height={40}
                            alt="copy"
                          />

                          <div className="d-flex align-items-center gap-2 ms-2">
                            <img
                              src="/images/png/users-list.png"
                              width={20}
                              alt="users"
                            />
                            <p>
                              {item.timesUsed} / {item.maxUses} uses
                            </p>
                          </div>

                          <div className="d-flex align-items-center gap-2 ms-2">
                            <img
                              src="/images/png/calendar-line.png"
                              width={25}
                              alt="calendar"
                            />
                            <p>
                              Coupon Start Date & Time{" "}
                              {item.startDate
                                ? new Date(item.startDate).toLocaleString(
                                    "en-GB"
                                  )
                                : "-"}
                            </p>
                          </div>
                          <div className="d-flex align-items-center gap-2 ms-2">
                            <img
                              src="/images/png/calendar-line.png"
                              width={25}
                              alt="calendar"
                            />
                            <p>
                              Coupon Expire Date & Time{" "}
                              {item.expiresAt
                                ? new Date(item.expiresAt).toLocaleString(
                                    "en-GB"
                                  )
                                : "-"}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <div className="d-flex align-items-center gap-2">
                            <p>Active</p>
                            <Form.Check
                              type="switch"
                              id={`active-switch-${item._id}`}
                              className="custom-switch"
                              checked={item.isActive}
                              disabled={new Date(item.expiresAt) < new Date()}
                              onChange={() =>
                                handleToggleActiveStatus(item._id)
                              }
                            />
                          </div>
                          <Link
                            to="#"
                            className="fs-16 fw-600"
                            style={{ color: "#60c4f3ff", textWrap: "nowrap" }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleViewUsers(item);
                            }}
                          >
                            View Users
                          </Link>
                          {/* edit */}
                          {item?.isFullyEditable ||
                          item?.isEndDateOnlyEditable ? (
                            <OverlayTrigger
                              disabled={true}
                              key="top"
                              placement="top"
                              overlay={<Tooltip id="tooltip-top">Edit</Tooltip>}
                            >
                              <Link
                                to={`#`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (item?.isFullyEditable) {
                                    toggleModelVisibility("couponCode", item);
                                  } else if (item?.isEndDateOnlyEditable) {
                                    toggleModelVisibility("couponCode", item);
                                  }
                                }}
                              >
                                <img
                                  src="/images/png/edit.png"
                                  className="pointer"
                                  width={35}
                                  alt="action"
                                />
                              </Link>
                            </OverlayTrigger>
                          ) : (
                            <Link
                              to={`#`}
                              onClick={(e) => {
                                e.preventDefault();
                              }}
                            >
                              <img
                                src="/images/png/edit.png"
                                className="pointer"
                                width={35}
                                alt="action"
                              />
                            </Link>
                          )}

                          {/* delete */}
                          <DeleteModal
                            onConfirm={() => handleDelete(item._id)}
                            loading={loading}
                            flag={"promo code"}
                          />
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center gap-3 mt-4 mb-2">
                        <p>Usage Progress</p>
                        <p>{Math.round(usageProgress)}%</p>
                      </div>
                      <ProgressBar now={usageProgress} />

                      <div className="d-flex justify-content-between align-items-center gap-3 mt-4">
                        <p>
                          Created{" "}
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString("en-GB")
                            : "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="card page-card mt-4">
                <div className="card-body">
                  <div className="No-date-found">
                    <img
                      src="/images/png/plus-1.png"
                      className="d-flex m-auto justify-content-center"
                      alt=""
                    />
                    <h5 className="text-center">No Coupon Codes Yet</h5>
                    <p className="text-center">
                      Create your first Coupon code to get started
                    </p>
                    <button
                      className="btn-primary fs-16 fw-600 m-auto d-flex mt-3"
                      onClick={() => toggleModelVisibility("couponCode")}
                    >
                      Generate Coupon Code
                    </button>
                  </div>
                </div>
              </div>
            )}

            {couponCodeList.length > 0 && (
              <CustomPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                limit={pagination.limit}
                onPageChange={(page) =>
                  setPagination((prev) => ({ ...prev, currentPage: page }))
                }
              />
            )}
          </div>
        </div>
      </main>

      {show.couponCode && (
        <CouponCodeModal
          show={show.couponCode}
          toggleModelVisibility={toggleModelVisibility}
          fetchCoupons={fetchCoupons}
          data={show?.data}
          isFullyEditable={show?.data?.isFullyEditable}
          isEndDateOnlyEditable={show?.data?.isEndDateOnlyEditable}
        />
      )}
      {show.redemptionsUsersList && (
        <RedemptionsUsersListViewModel
          show={show.redemptionsUsersList}
          toggleModelVisibility={toggleModelVisibility}
          data={show?.data}
        />
      )}
    </Layout>
  );
}

export default CouponCode;
